import { Button, useTheme } from '@material-ui/core'
import React from 'react'
import { Container } from '../../components'
import { Check } from '@material-ui/icons'
import { axiosInstance } from '../../helpers'
import {
    EDIT_VENDOR_SUBSCRIPTION_STATUS,
    ListVendor,
    PlanStatus,
} from '../../models'
import { hasPermission } from '../../models/Users/services'
import { useUser } from '../../hooks'
import { useStripe } from '../../hooks/useStripe'

interface Props {
    vendor: ListVendor
}

export const VendorPlans = (props: Props) => {
    const { vendor } = props

    const { workspaceUser } = useUser()

    const {
        createStripePortalSession,
        createStripeCheckoutSession,
    } = useStripe()

    const theme = useTheme()

    const PlanStyle: React.CSSProperties = {
        height: '560px',
        width: '250px',
        border: `1px solid ${theme.palette.lightGrey.main}`,
        borderRadius: '10px',
    }

    const IconStyle: React.CSSProperties = {
        padding: theme.spacing(0.3),
        marginRight: theme.spacing(1),
    }

    const disabled = !hasPermission(
        workspaceUser,
        EDIT_VENDOR_SUBSCRIPTION_STATUS,
    )

    const hasNoPlan = vendor.plan === PlanStatus.NO_PLAN || vendor.plan === null
    const hasPremium = vendor.plan === PlanStatus.PREMIUM

    return (
        <Container
            flex={1}
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            style={{
                margin: theme.spacing(3, 4, 4, 6),
                gap: theme.spacing(4),
            }}
        >
            <Container
                style={{
                    ...PlanStyle,
                    backgroundColor: theme.palette.primary.dark,
                    alignItems: 'center',
                    padding: theme.spacing(4, 3),
                    paddingTop: theme.spacing(10),
                    paddingBottom: theme.spacing(8),
                }}
                direction="column"
            >
                <Container
                    style={{
                        fontWeight: 700,
                        fontSize: '22px',
                        color: 'white',
                        marginBottom: theme.spacing(2),
                    }}
                >
                    Choose your Plan!
                </Container>
                <Container
                    style={{
                        fontWeight: 500,
                        fontSize: '15px',
                        color: 'white',
                        textAlign: 'center',
                        padding: theme.spacing(0, 2),
                    }}
                >
                    Welcome to EZ Turn plans! Here you can choose which plan
                    best suits you.
                </Container>
            </Container>
            <Container
                style={{
                    ...PlanStyle,
                    alignItems: 'center',
                    padding: theme.spacing(3, 3),
                    borderColor: hasPremium
                        ? theme.palette.primary.dark
                        : hasNoPlan
                        ? theme.palette.darkBlue.main
                        : theme.palette.lightGrey.main,
                    borderWidth: hasPremium ? '3px' : '1px',
                    minHeight: hasNoPlan ? '600px' : '560px',
                    height: 'auto',
                    boxSizing: 'border-box',
                }}
                direction="column"
            >
                {hasNoPlan && (
                    <Container
                        style={{
                            fontWeight: 500,
                            fontSize: '15px',
                            backgroundColor: theme.palette.darkBlue.main,
                            color: 'white',
                            flex: 1,
                            padding: theme.spacing(1.5, 0),
                            width: '100%',
                            justifyContent: 'center',
                            borderTopLeftRadius: '10px',
                            borderTopRightRadius: '10px',
                        }}
                    >
                        Recommended
                    </Container>
                )}
                <Container
                    style={{
                        fontWeight: 600,
                        fontSize: '20px',
                        marginTop: theme.spacing(3),
                    }}
                >
                    Premium
                </Container>
                <Container
                    style={{
                        marginTop: theme.spacing(3),
                        alignItems: 'center',
                    }}
                >
                    <span
                        style={{
                            fontWeight: 600,
                            fontSize: '18px',
                        }}
                    >
                        $
                    </span>
                    <span
                        style={{
                            fontWeight: 600,
                            fontSize: '40px',
                            margin: theme.spacing(0, 0.5, 0, 0.5),
                        }}
                    >
                        19.99
                    </span>
                    <span
                        style={{
                            fontWeight: 600,
                            fontSize: '18px',
                        }}
                    >
                        /mo.
                    </span>
                </Container>
                <Container
                    style={{
                        fontWeight: 400,
                        fontSize: '15px',
                        color: theme.palette.darkGreen.main,
                        textAlign: 'center',
                        marginTop: theme.spacing(4),
                        padding: theme.spacing(0, 2),
                    }}
                >
                    Premium increases your visibility: get top-of-the-list
                    placement and featured vendor status so properties see you
                    first.
                </Container>
                <Container
                    style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        color: theme.palette.darkGreen.main,
                        marginTop: theme.spacing(4),
                        padding: theme.spacing(0, 1),
                    }}
                    direction="column"
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <Check style={{ ...IconStyle }} /> Vendor Verification
                        on NVL
                    </Container>
                    <Container
                        style={{
                            marginBottom: theme.spacing(2),
                            alignItems: 'center',
                        }}
                    >
                        <Check style={{ ...IconStyle }} /> Featured vendor — top
                        of the list
                    </Container>
                    <Container
                        style={{
                            marginBottom: theme.spacing(2),
                            alignItems: 'center',
                        }}
                    >
                        <Check style={{ ...IconStyle }} /> Access to National
                        Property List
                    </Container>
                </Container>
                <Container flex={1} />
                <Container
                    style={{
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(3),
                    }}
                >
                    <Button
                        variant="contained"
                        style={{
                            color: hasPremium
                                ? 'white'
                                : theme.palette.primary.dark,
                            textTransform: 'none',
                            border: `1px solid ${theme.palette.primary.dark}`,
                            backgroundColor: hasPremium
                                ? theme.palette.primary.dark
                                : 'white',
                            width: '150px',
                        }}
                        size="large"
                        onClick={() => {
                            if (!hasNoPlan) {
                                const body = {
                                    return_url: window.location.href,
                                }
                                createStripePortalSession(body)
                            } else {
                                const body = {
                                    plan_type: PlanStatus.PREMIUM,
                                    success_url: window.location.href,
                                }
                                createStripeCheckoutSession(body)
                            }
                        }}
                        disabled={disabled}
                    >
                        {hasPremium ? 'Current' : 'Select'}
                    </Button>
                </Container>
            </Container>
        </Container>
    )
}
